(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const CART_SECTION_ID = 'cart-drawer';

  /* ------------------------------------------------------------------------
     Drawers (mobile menu + cart)
     ------------------------------------------------------------------------ */

  let lastFocused = null;

  function openDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    document
      .querySelectorAll(`[aria-controls="${id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', 'true'));
    const focusTarget = drawer.querySelector('[data-drawer-close]:not(.drawer__overlay)');
    if (focusTarget) focusTarget.focus();
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document
      .querySelectorAll(`[aria-controls="${drawer.id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (!document.querySelector('.drawer.is-open')) {
      document.body.classList.remove('overflow-hidden');
    }
    if (lastFocused) lastFocused.focus();
  }

  function closeAllDrawers() {
    document.querySelectorAll('.drawer.is-open').forEach(closeDrawer);
  }

  /* ------------------------------------------------------------------------
     Search overlay
     ------------------------------------------------------------------------ */

  function toggleSearch(forceOpen) {
    const overlay = document.getElementById('SearchOverlay');
    if (!overlay) return;
    const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : overlay.hidden;
    overlay.hidden = !shouldOpen;
    document
      .querySelectorAll('[data-search-toggle]')
      .forEach((el) => el.setAttribute('aria-expanded', String(shouldOpen)));
    if (shouldOpen) {
      const input = overlay.querySelector('input[type="search"]');
      if (input) input.focus();
    }
  }

  /* ------------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------------ */

  function setCartError(message) {
    document.querySelectorAll('[data-cart-error]').forEach((el) => {
      el.textContent = message || '';
      el.hidden = !message;
    });
  }

  function renderCartDrawer(sections) {
    if (!sections || !sections[CART_SECTION_ID]) return;
    const doc = new DOMParser().parseFromString(sections[CART_SECTION_ID], 'text/html');
    const nextInner = doc.querySelector('[data-cart-drawer-inner]');
    const currentInner = document.querySelector('[data-cart-drawer-inner]');
    if (nextInner && currentInner) {
      currentInner.replaceWith(nextInner);
      const count = Number(nextInner.dataset.itemCount || 0);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count > 0 ? String(count) : '';
      });
    }
  }

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || theme.strings.cartError);
    }
    return data;
  }

  async function refreshCartDrawer() {
    const response = await fetch(`${theme.routes.root}?sections=${CART_SECTION_ID}`);
    renderCartDrawer(await response.json());
  }

  async function addToCart(items) {
    try {
      const data = await cartRequest(`${theme.routes.cartAdd}.js`, {
        items,
        sections: CART_SECTION_ID,
        sections_url: window.location.pathname,
      });
      renderCartDrawer(data.sections);
      setCartError('');
    } catch (error) {
      await refreshCartDrawer();
      setCartError(error.message);
    }
    openDrawer('CartDrawer');
  }

  async function changeCartLine(line, quantity, itemEl) {
    if (itemEl) itemEl.classList.add('is-loading');
    try {
      const data = await cartRequest(`${theme.routes.cartChange}.js`, {
        line,
        quantity,
        sections: CART_SECTION_ID,
        sections_url: window.location.pathname,
      });
      if (document.querySelector('[data-cart-page]')) {
        window.location.reload();
        return;
      }
      renderCartDrawer(data.sections);
      setCartError('');
    } catch (error) {
      if (itemEl) itemEl.classList.remove('is-loading');
      setCartError(error.message);
    }
  }

  function formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    const format = theme.moneyFormat || '${{amount}}';
    const amount = value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const amountNoDecimals = Math.round(value).toLocaleString(undefined);
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  async function handleProductFormSubmit(form) {
    const submit = form.querySelector('[type="submit"]');
    const idInput = form.querySelector('[name="id"]');
    const qtyInput = form.querySelector('[name="quantity"]');
    if (!idInput || !idInput.value) return;

    const minQty = Number(qtyInput?.getAttribute('min')) || 1;
    const quantity = qtyInput
      ? Math.max(minQty, Number(qtyInput.value) || minQty)
      : minQty;

    const item = {
      id: Number(idInput.value),
      quantity,
    };

    const properties = {};
    form.querySelectorAll('[name^="properties["]').forEach((input) => {
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (match && input.value) properties[match[1]] = input.value;
    });
    if (Object.keys(properties).length) item.properties = properties;

    const originalLabel = submit ? submit.textContent : '';
    if (submit) {
      submit.setAttribute('aria-disabled', 'true');
      submit.textContent = theme.strings.adding;
    }

    await addToCart([item]);

    if (submit) {
      submit.removeAttribute('aria-disabled');
      submit.textContent = originalLabel;
    }
  }

  /* ------------------------------------------------------------------------
     Product variant picker
     ------------------------------------------------------------------------ */

  function syncBundleSelection(root, unitPrice) {
    const bundles = root.querySelector('[data-product-bundles]');
    if (!bundles) return;

    const selected = bundles.querySelector('input[name="bundle_tier"]:checked');
    if (!selected) return;

    const buy = Number(selected.dataset.buy) || 0;
    const total = Number(selected.dataset.total) || buy;
    const label = selected.dataset.label || '';
    const variantId = selected.dataset.variantId || '';
    const qtyInput = root.querySelector('[data-bundle-quantity]');
    const propInput = root.querySelector('[data-bundle-property]');
    const idInput =
      root.querySelector('[data-bundle-variant-input]') ||
      root.querySelector('form[data-product-form] [name="id"]');
    const useCustomPricing = bundles.dataset.customBundlePricing === 'true';

    if (variantId && idInput) {
      idInput.value = variantId;
      if (qtyInput) {
        qtyInput.value = '1';
        qtyInput.min = '1';
      }
    } else if (qtyInput) {
      qtyInput.value = String(total);
    }
    if (propInput) propInput.value = label;

    bundles.querySelectorAll('.product-bundles__option').forEach((option) => {
      const input = option.querySelector('input[name="bundle_tier"]');
      option.classList.toggle('is-selected', Boolean(input && input.checked));
    });

    const selectedPay = Number(selected.dataset.payCents);
    const selectedCompare = Number(selected.dataset.compareCents);
    const hasSelectedCustom =
      useCustomPricing && Number.isFinite(selectedPay) && selectedPay > 0;

    if (hasSelectedCustom || typeof unitPrice === 'number') {
      if (!useCustomPricing && typeof unitPrice === 'number') {
        bundles.querySelectorAll('input[name="bundle_tier"]').forEach((input) => {
          const option = input.closest('.product-bundles__option');
          if (!option) return;
          const payEl = option.querySelector('[data-bundle-pay]');
          const compareEl = option.querySelector('[data-bundle-compare]');
          const tierBuy = Number(input.dataset.buy) || 0;
          const tierTotal = Number(input.dataset.total) || tierBuy;
          if (payEl) payEl.textContent = formatMoney(unitPrice * tierBuy);
          if (compareEl) compareEl.textContent = formatMoney(unitPrice * tierTotal);
        });
      }

      const payCents = hasSelectedCustom
        ? selectedPay
        : unitPrice * buy;
      const compareCents = hasSelectedCustom && Number.isFinite(selectedCompare)
        ? selectedCompare
        : unitPrice * total;

      const priceEl = root.querySelector('[data-product-price]');
      if (priceEl) {
        const onSale = compareCents > payCents;
        priceEl.innerHTML = `
          <div class="price${onSale ? ' price--on-sale' : ''}">
            <span class="price__current">${formatMoney(payCents)}</span>
            ${onSale ? `<s class="price__compare">${formatMoney(compareCents)}</s>` : ''}
          </div>
        `;
      }

      const stickyLabel = document.querySelector('[data-sticky-atc-label]');
      const stickyPrice = document.querySelector('[data-sticky-atc-price]');
      if (stickyLabel) stickyLabel.textContent = label;
      if (stickyPrice) stickyPrice.textContent = formatMoney(payCents);
    }
  }

  function initProductBundles(root) {
    const bundles = root.querySelector('[data-product-bundles]');
    if (!bundles) return;

    bundles.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="bundle_tier"]')) return;
      const unitPrice = Number(root.dataset.unitPrice);
      syncBundleSelection(root, Number.isFinite(unitPrice) ? unitPrice : undefined);
    });

    const unitPrice = Number(root.dataset.unitPrice);
    syncBundleSelection(root, Number.isFinite(unitPrice) ? unitPrice : undefined);
  }

  function initVariantPicker(root) {
    const form = root.querySelector('form[data-product-form]');
    const idInput = form ? form.querySelector('[name="id"]') : null;
    const submit = form ? form.querySelector('[type="submit"]') : null;
    const priceEl = root.querySelector('[data-product-price]');
    const dataEl = root.querySelector('[data-variants-json]');
    const variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    const optionGroups = Array.from(root.querySelectorAll('[data-option-position]'));

    function selectedOptions() {
      return optionGroups.map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function markUnavailable(selected) {
      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('input').forEach((input) => {
          const candidate = selected.slice();
          candidate[groupIndex] = input.value;
          const match = variants.find((v) => v.options.every((opt, i) => opt === candidate[i]));
          const label = root.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    function update() {
      const selected = selectedOptions();
      const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      markUnavailable(selected);

      if (!variant) {
        if (submit) {
          submit.disabled = true;
          submit.textContent = theme.strings.unavailable;
        }
        const stickyButton = document.querySelector('[data-sticky-atc-button]');
        if (stickyButton) {
          stickyButton.disabled = true;
          stickyButton.textContent = theme.strings.unavailable;
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      root.dataset.unitPrice = String(variant.price);

      if (submit) {
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      }

      const stickyButton = document.querySelector('[data-sticky-atc-button]');
      if (stickyButton) {
        stickyButton.disabled = !variant.available;
        stickyButton.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      }

      const priceTemplate = root.querySelector(`template[data-variant-price="${variant.id}"]`);
      if (priceEl && priceTemplate && !root.querySelector('[data-product-bundles]')) {
        priceEl.innerHTML = priceTemplate.innerHTML;
      }

      if (variant.featured_media_id) {
        const media = root.querySelector(`[data-media-id="${variant.featured_media_id}"]`);
        const gallery = root.querySelector('[data-product-gallery]');
        if (media && gallery && gallery.scrollWidth > gallery.clientWidth) {
          gallery.scrollTo({ left: media.offsetLeft, behavior: 'smooth' });
        }
      }

      syncBundleSelection(root, variant.price);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    root.addEventListener('change', (event) => {
      if (event.target.closest('[data-option-position]')) update();
    });

    if (variants.length) markUnavailable(selectedOptions());
    initProductBundles(root);
  }

  /* ------------------------------------------------------------------------
     Collection filters
     ------------------------------------------------------------------------ */

  function applyFilters(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    window.location.href = `${form.action}?${params.toString()}`;
  }

  const desktopFilters = window.matchMedia('(min-width: 990px)');

  function syncFiltersSidebar() {
    document.querySelectorAll('[data-filters-drawer]').forEach((drawer) => {
      const panel = drawer.querySelector('.drawer__panel');
      if (desktopFilters.matches) {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        if (panel) {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-modal');
        }
        if (!document.querySelector('.drawer.is-open')) document.body.classList.remove('overflow-hidden');
      } else {
        if (!drawer.classList.contains('is-open')) drawer.setAttribute('aria-hidden', 'true');
        if (panel) {
          panel.setAttribute('role', 'dialog');
          panel.setAttribute('aria-modal', 'true');
        }
      }
    });
  }

  function setHeaderHeight() {
    const header = document.querySelector('.section-header');
    const isSticky = header && getComputedStyle(header).position === 'sticky';
    document.documentElement.style.setProperty('--header-height', `${isSticky ? header.offsetHeight : 0}px`);
  }

  /* ------------------------------------------------------------------------
     Carousels
     ------------------------------------------------------------------------ */

  function initCarousel(carousel) {
    const track = carousel.querySelector('[data-carousel-track]');
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    if (!track) return;

    const updateButtons = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll;
    };

    const step = () => track.clientWidth * 0.8;
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step() }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step() }));
    track.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
  }

  function initProductGallery(root) {
    const wrap = root.querySelector('.product__gallery-wrap') || root;
    const gallery = wrap.querySelector('[data-product-gallery]');
    const prev = wrap.querySelector('[data-gallery-prev]');
    const next = wrap.querySelector('[data-gallery-next]');
    if (!gallery || !prev || !next) return;

    const slides = Array.from(gallery.querySelectorAll('.product__media'));
    const dots = Array.from(wrap.querySelectorAll('[data-gallery-dot]'));
    if (slides.length < 2) return;

    let index = 0;

    const update = () => {
      prev.disabled = index <= 0;
      next.disabled = index >= slides.length - 1;
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const goTo = (nextIndex) => {
      index = Math.min(slides.length - 1, Math.max(0, nextIndex));
      const slide = slides[index];
      gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      update();
    };

    prev.addEventListener('click', () => goTo(index - 1));
    next.addEventListener('click', () => goTo(index + 1));
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => goTo(i));
    });

    gallery.addEventListener(
      'scroll',
      () => {
        const center = gallery.scrollLeft + gallery.clientWidth / 2;
        let closest = 0;
        let closestDistance = Infinity;
        slides.forEach((slide, i) => {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
          const distance = Math.abs(slideCenter - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = i;
          }
        });
        index = closest;
        update();
      },
      { passive: true }
    );

    update();
  }

  function initBeforeAfter(root) {
    const range = root.querySelector('[data-before-after-range]');
    if (!range) return;

    const setPosition = (value) => {
      const clamped = Math.min(100, Math.max(0, Number(value) || 0));
      root.style.setProperty('--ba-position', `${clamped}%`);
      range.value = String(clamped);
    };

    setPosition(range.value);

    range.addEventListener('input', () => setPosition(range.value));
    range.addEventListener('change', () => setPosition(range.value));
  }

  function initResultsMeters(root) {
    const meters = Array.from(root.querySelectorAll('[data-results-meter]'));
    if (!meters.length) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const animateMeter = (meter) => {
      const target = Math.min(100, Math.max(0, Number(meter.dataset.percent) || 0));
      const valueEl = meter.querySelector('[data-results-meter-value]');
      meter.style.setProperty('--progress', String(target));

      if (!valueEl) return;
      if (prefersReducedMotion) {
        valueEl.textContent = String(Math.round(target));
        return;
      }

      const duration = 1100;
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        valueEl.textContent = String(Math.round(target * eased));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      meters.forEach(animateMeter);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateMeter(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    meters.forEach((meter) => observer.observe(meter));
  }

  function initTestimonialsCarousel(root) {
    const slides = Array.from(root.querySelectorAll('[data-testimonials-slide]'));
    if (slides.length < 2) return;

    const viewport = root.querySelector('.testimonials__viewport');
    const prev = root.querySelector('[data-testimonials-prev]');
    const next = root.querySelector('[data-testimonials-next]');
    const dots = Array.from(root.querySelectorAll('[data-testimonials-dot]'));
    let index = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active'))
    );

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => show(i));
    });

    if (viewport) {
      let startX = 0;
      let startY = 0;
      let tracking = false;

      viewport.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) return;
          tracking = true;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      viewport.addEventListener(
        'touchend',
        (event) => {
          if (!tracking || !event.changedTouches.length) return;
          tracking = false;
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
          show(dx < 0 ? index + 1 : index - 1);
        },
        { passive: true }
      );
    }
  }

  /* ------------------------------------------------------------------------
     Announcement bar rotation
     ------------------------------------------------------------------------ */

  function initAnnouncementRotator(bar) {
    const items = Array.from(bar.querySelectorAll('.announcement-bar__item'));
    if (items.length === 0) return;
    items[0].classList.add('is-active');
    if (items.length < 2) return;
    const intervalMs = (Number(bar.dataset.interval) || 5) * 1000;
    let index = 0;
    setInterval(() => {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, intervalMs);
  }

  /* ------------------------------------------------------------------------
     Init + event delegation
     ------------------------------------------------------------------------ */

  function initStickyAtc(root) {
    const sticky = document.querySelector('[data-sticky-atc]');
    const trigger = root.querySelector('[data-product-atc]');
    const stickyButton = sticky?.querySelector('[data-sticky-atc-button]');
    const form = root.querySelector('form[data-product-form]');
    if (!sticky || !trigger || !stickyButton || !form) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      },
      { threshold: 0 }
    );

    observer.observe(trigger);

    stickyButton.addEventListener('click', () => {
      if (stickyButton.disabled) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });
  }

  function initWithin(scope) {
    scope.querySelectorAll('[data-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-testimonials-carousel]').forEach(initTestimonialsCarousel);
    scope.querySelectorAll('[data-before-after]').forEach(initBeforeAfter);
    scope.querySelectorAll('[data-results]').forEach(initResultsMeters);
    scope.querySelectorAll('[data-announcement-rotator]').forEach(initAnnouncementRotator);
    scope.querySelectorAll('[data-product]').forEach((root) => {
      initVariantPicker(root);
      initProductGallery(root);
      initStickyAtc(root);
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;

    const drawerOpen = target.closest('[data-drawer-open]');
    if (drawerOpen) {
      event.preventDefault();
      openDrawer(drawerOpen.dataset.drawerOpen);
      return;
    }

    const drawerClose = target.closest('[data-drawer-close]');
    if (drawerClose) {
      event.preventDefault();
      closeDrawer(drawerClose.closest('.drawer'));
      return;
    }

    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      openDrawer('CartDrawer');
      return;
    }

    const searchToggle = target.closest('[data-search-toggle]');
    if (searchToggle) {
      event.preventDefault();
      toggleSearch();
      return;
    }

    const lineChange = target.closest('[data-cart-line-change]');
    if (lineChange) {
      event.preventDefault();
      changeCartLine(
        Number(lineChange.dataset.line),
        Number(lineChange.dataset.quantity),
        lineChange.closest('.cart-item')
      );
    }
  });

  document.addEventListener('submit', (event) => {
    const filtersForm = event.target.closest('form[data-filters-form]');
    if (filtersForm) {
      event.preventDefault();
      applyFilters(filtersForm);
      return;
    }

    const form = event.target.closest('form[data-product-form]');
    if (!form) return;
    event.preventDefault();
    handleProductFormSubmit(form);
  });

  document.addEventListener('change', (event) => {
    const filtersForm = event.target.closest('form[data-filters-form]');
    if (filtersForm) applyFilters(filtersForm);

    const insuranceToggle = event.target.closest('[data-cart-insurance-toggle]');
    if (!insuranceToggle || insuranceToggle.disabled) return;

    const onCartPage = Boolean(document.querySelector('[data-cart-page]'));

    if (insuranceToggle.checked) {
      const variantId = Number(insuranceToggle.dataset.variantId);
      if (!variantId) return;
      insuranceToggle.disabled = true;
      addToCart([{ id: variantId, quantity: 1 }])
        .then(() => {
          if (onCartPage) window.location.reload();
        })
        .finally(() => {
          insuranceToggle.disabled = false;
        });
      return;
    }

    const line = Number(insuranceToggle.dataset.line);
    if (!line) return;
    insuranceToggle.disabled = true;
    changeCartLine(line, 0).finally(() => {
      insuranceToggle.disabled = false;
    });
  });

  window.addEventListener('resize', setHeaderHeight);
  setHeaderHeight();

  desktopFilters.addEventListener('change', syncFiltersSidebar);
  syncFiltersSidebar();

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllDrawers();
    toggleSearch(false);
  });

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));

  initWithin(document);
})();
